
import { connectionName, getRegionPickerConnection, RegionPickerDispatchers } from '@dh-io-emh/region-picker-store';
import { defineComponent, nextTick, onBeforeUnmount, onMounted, PropType, ref, toRefs } from 'vue';
import RegionPickerPopupComponent from './components/region-picker-popup/region-picker-popup.vue';
import { DialogParameters, getAemComponentData } from './utils/aem.util';
import { MarketConfig } from './utils/config-service';
import { useTracking } from './tracking.composable';

export default defineComponent({
  name: 'DhIoEmhApp',
  components: {
    RegionPickerPopupComponent,
  },
  props: {
    config: {
      type: Object as PropType<MarketConfig>,
      required: true,
    },
  },
  setup(props) {
    const region = ref('');
    const isVisible = ref(false);
    const isRequired = ref(false);
    const el = ref<HTMLElement>();
    const { trackSetRegion, trackOpenFlyout } = useTracking();
    const { config } = toRefs(props);

    let unsubscribeFromRegionPickerConnection!: () => void;
    let regionPickerConnectionDispatchers!: RegionPickerDispatchers;
    let regionPickerConnection;

    const closeFlyoutListener = () => {
      regionPickerConnectionDispatchers.setOpenState(false);
    };

    const getInitialisationData = async (componentId: string | null) => {
      const aemComponentData = await getAemComponentData(componentId);
      const { isOpenOnStart, isRequired }: DialogParameters = aemComponentData.payload as DialogParameters;
      return {
        isOpenOnStart,
        isRequired,
      };
    };

    onMounted(async () => {
      //close region picker, when user clicks on dark page overlay
      //TODO: Currently not possible. closing of overlay is controlled by OWC. Find a way to disable owc overlay close on click
      window.addEventListener('owc-header-flyout-close', closeFlyoutListener);
      // get configuration from region picker store
      regionPickerConnection = getRegionPickerConnection(config?.value?.region);
      regionPickerConnectionDispatchers = await regionPickerConnection.getRegisteredDispatchers();
      const store = regionPickerConnection.getStoreReference();

      // get aem configuration from seamless store
      const element = document.querySelector('dh-io-emh-region-picker') || null;
      const componentId = (element && element.getAttribute('component-id')) || null;
      const { isOpenOnStart, isRequired: isInitialRequired } = await getInitialisationData(componentId);

      isRequired.value = isInitialRequired;

      unsubscribeFromRegionPickerConnection = store.subscribe(
        connectionName,
        async ({ region: storeRegion, isOpen }: any) => {
          // can be triggered with the following command inside of the browser console:
          // window.seamlessStore.getConnection("DH-IO-OWCC_REGION_PICKER_CONNECTION").then((connection)=>{connection.setOpenState(true)})
          // can be queried with the following command:
          // window.seamlessStore.store.getState()["DH-IO-OWCC_REGION_PICKER_CONNECTION"].isOpen

          region.value = storeRegion;
          isVisible.value = isOpen;
          if (isRequired.value) {
            await nextTick();
            openIfNoRegion();
          }
        },
      ).unsubscribe;

      if (isOpenOnStart) {
        openIfNoRegion();
      }
    });

    // open region picker flyout when required and no region entered yet
    const openIfNoRegion = async () => {
      if (!isVisible.value && !region.value) {
        // open region picker flyout
        toggleRegionPicker(true);
      }
    };

    onBeforeUnmount(() => {
      unsubscribeFromRegionPickerConnection();
      document.removeEventListener('owc-header-flyout-close', closeFlyoutListener);
    });

    const setRegion = (region: string) => {
      regionPickerConnectionDispatchers.setRegion(region);
      trackSetRegion();
    };

    const toggleRegionPicker = (show?: boolean) => {
      // check if parameter is a boolean to filter cases where the function gets called without parameter
      // or is used as a callback for native click events
      const open = typeof show == 'boolean' ? show : !isVisible.value;
      regionPickerConnectionDispatchers.setOpenState(open);

      if (open) {
        trackOpenFlyout();
      }
    };

    return {
      isRequired,
      region,
      isVisible,
      toggleRegionPicker,
      setRegion,
      el,
    };
  },
});
